/*
<summary>
   This functional component is all about rendering the check items panel (headers and their check items) for a single inspection tab, including template import and header/item reordering.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../../contexts/store-provider';
import { IToolCheckItem } from '../../../../models/forms/IToolInspection';
import {
  DownIcon,
  PlusIcon,
  RemoveIcon,
  UpIcon,
} from '../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../translations/format-message';

interface IHeaderGroup {
  localId: string;
  Title: string;
  IsEditing: boolean;
  Items: IToolCheckItem[];
}

interface IProps {
  checkItems: IToolCheckItem[];
  headers?: { Title: string; Items: IToolCheckItem[] }[];
  onChange: (
    checkItems: IToolCheckItem[],
    headers?: { Title: string; Items: IToolCheckItem[] }[]
  ) => void;
  submitAttempted?: boolean;
}

let uidCounter = 0;
const uid = () => `tci-${Date.now()}-${uidCounter++}`;

/**
 * This function is used to group a flat list of check items into local header groups, preserving the order headers were first encountered.
 * @param items: Flat list of check items
 */
const groupByHeader = (items: IToolCheckItem[]): IHeaderGroup[] => {
  const groups: Record<string, IToolCheckItem[]> = {};
  const order: string[] = [];
  items.forEach((item) => {
    const title = item.HeaderTitle || '';
    if (!groups[title]) {
      groups[title] = [];
      order.push(title);
    }
    groups[title].push(item);
  });
  return order.map((title) => ({
    localId: uid(),
    Title: title,
    IsEditing: false,
    Items: groups[title],
  }));
};

/**
 * This function is used to normalize a check item template's raw headers (which can come as either a flat row array or a nested tree) into a consistent Title/Items shape.
 * @param rawHeaders: Raw headers as returned by the template API
 */
const normalizeTemplateHeaders = (rawHeaders: any[]) => {
  if (!rawHeaders || rawHeaders.length === 0) return [];

  // This handles the flat array of row items shape (with HeaderName & CheckitemText), as returned by the API JSON.
  if (
    rawHeaders[0] &&
    (rawHeaders[0].HeaderName !== undefined || rawHeaders[0].CheckitemText !== undefined)
  ) {
    const groupMap: Record<string, { CheckItemMessage: string; IsChecked: boolean }[]> = {};
    const groupOrder: string[] = [];

    rawHeaders.forEach((item: any) => {
      const title = (item.HeaderName || item.Title || '').trim();
      const text = (item.CheckitemText || item.CheckItemMessage || '').trim();
      const isChecked = !!(item.AllowTextEntry ?? item.IsChecked ?? item.IsInfo);
      if (!groupMap[title]) {
        groupMap[title] = [];
        groupOrder.push(title);
      }
      if (text) {
        groupMap[title].push({ CheckItemMessage: text, IsChecked: isChecked });
      }
    });

    return groupOrder.map((title) => ({
      Title: title,
      Items: groupMap[title],
    }));
  }

  // This handles the nested tree structure shape, with Title / CheckItems.
  return rawHeaders.map((h: any) => ({
    Title: h.Title || h.HeaderName || '',
    Items: (h.CheckItems || h.CheckItemMessage || []).map((ci: any) => ({
      CheckItemMessage: typeof ci === 'string' ? ci : ci.CheckItemMessage || ci.CheckitemText || '',
      IsChecked: !!(ci.IsChecked ?? ci.AllowTextEntry ?? ci.IsInfo),
    })),
  }));
};

const ToolCheckItemsPanel = observer(({
  checkItems,
  headers: headersProp,
  onChange,
  submitAttempted,
}: IProps) => {
  let globalItemNo = 0;
  const { toolCheckItemTemplateStore } = useStore();
  const {
    allToolCheckItemTemplates,
    allToolCheckItemTemplateOptions,
    GetAllCheckItemTemplatesService,
    GetCheckItemTemplateByIdService,
    checkItemTemplateDetail,
  } = toolCheckItemTemplateStore;

  const [headers, setHeaders] = useState<IHeaderGroup[]>(() =>
    headersProp && headersProp.length > 0
      ? headersProp.map((h) => ({
          localId: uid(),
          Title: h.Title,
          IsEditing: false,
          Items: h.Items,
        }))
      : groupByHeader(checkItems)
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(-1);

  /**
   * This useEffect is used to load the check item templates dropdown once, on mount.
   */
  useEffect(() => {
    GetAllCheckItemTemplatesService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This function is used to update local header state and push the equivalent flat check items (plus grouped headers) up to the parent via onChange.
   * @param newHeaders: New header groups to persist
   */
  const persist = (newHeaders: IHeaderGroup[]) => {
    setHeaders(newHeaders);
    const flatItems = newHeaders.flatMap((h) =>
      h.Items.map((item) => ({ ...item, HeaderTitle: h.Title }))
    );
    onChange(
      flatItems,
      newHeaders.map((h) => ({ Title: h.Title, Items: h.Items }))
    );
  };

  /**
   * This function is used to import a template's headers/items, merging them into the existing headers (skipping items whose text already exists under the same header) and reporting how many were actually added.
   * @param rawHeaders: Raw headers from the selected template
   */
  const applyTemplateData = (rawHeaders: any[]) => {
    const importedHeaders = normalizeTemplateHeaders(rawHeaders);
    if (importedHeaders.length === 0) return;

    let merged = [...headers];
    let addedCount = 0;
    importedHeaders.forEach((ih) => {
      const existingIdx = merged.findIndex(
        (h) => h.Title.trim().toLowerCase() === ih.Title.trim().toLowerCase()
      );
      if (existingIdx >= 0) {
        const existingTexts = new Set(
          merged[existingIdx].Items.map((i) =>
            i.CheckItemMessage.trim().toLowerCase()
          )
        );
        const newItems = ih.Items.filter(
          (i) => !existingTexts.has(i.CheckItemMessage.trim().toLowerCase())
        );
        addedCount += newItems.length;
        merged[existingIdx] = {
          ...merged[existingIdx],
          Items: [
            ...merged[existingIdx].Items,
            ...newItems.map((item) => ({ ...item, HeaderTitle: ih.Title, IsDisabled: false })),
          ],
        };
      } else {
        merged.push({
          localId: uid(),
          Title: ih.Title,
          IsEditing: false,
          Items: ih.Items.map((item) => ({ ...item, HeaderTitle: ih.Title, IsDisabled: false })),
        });
        addedCount += ih.Items.length;
      }
    });

    persist(merged);
    if (addedCount > 0) {
      toast.success(formatMessage('template_check_items_added', { count: addedCount }));
    } else {
      toast(formatMessage('duplicate_items_ignored'));
    }
    setSelectedTemplateId(-1);
  };

  /**
   * This useEffect is used to apply the selected template's headers once its details finish loading from the store.
   */
  useEffect(() => {
    if (selectedTemplateId > 0 && checkItemTemplateDetail?.Headers) {
      applyTemplateData(checkItemTemplateDetail.Headers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkItemTemplateDetail]);

  /**
   * This function is used to fetch a check item template's details once the user picks it from the dropdown.
   * @param e: Select change event
   */
  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = Number(e.target.value);
    if (templateId <= 0) {
      setSelectedTemplateId(-1);
      return;
    }

    setSelectedTemplateId(templateId);
    GetCheckItemTemplateByIdService(templateId);
  };

const DEFAULT_HEADER_TITLE = 'Default';

/**
 * This function is used to build a unique "Default" header title, appending a numeric suffix if "Default" (or "DefaultN") is already taken.
 * @param currentHeaders: Existing header groups to check against
 */
const getNextDefaultHeaderTitle = (currentHeaders: IHeaderGroup[]): string => {
  const existingTitles = new Set(
    currentHeaders.map((h) => (h.Title || '').trim().toLowerCase())
  );
  const base = DEFAULT_HEADER_TITLE.toLowerCase();

  if (!existingTitles.has(base)) {
    return DEFAULT_HEADER_TITLE;
  }

  for (let i = 1; i <= 100; i++) {
    const candidate = `${DEFAULT_HEADER_TITLE}${i}`;
    if (!existingTitles.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return `${DEFAULT_HEADER_TITLE}${Date.now()}`;
};

  /**
   * This function is used to add a new header group with a unique default title.
   */
  const handleAddHeader = () => {
    const nextTitle = getNextDefaultHeaderTitle(headers);
    persist([
      ...headers,
      { localId: uid(), Title: nextTitle, IsEditing: false, Items: [] },
    ]);
  };

  const handleHeaderTitleChange = (localId: string, value: string) => {
    setHeaders((prev) =>
      prev.map((h) => (h.localId === localId ? { ...h, Title: value } : h))
    );
  };

  const handleHeaderTitleBlur = (localId: string) => {
    persist(
      headers.map((h) =>
        h.localId === localId ? { ...h, IsEditing: false } : h
      )
    );
  };

  const handleDeleteHeader = (localId: string) => {
    persist(headers.filter((h) => h.localId !== localId));
  };

  const handleMoveHeader = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= headers.length) return;
    const newHeaders = [...headers];
    [newHeaders[index], newHeaders[newIndex]] = [
      newHeaders[newIndex],
      newHeaders[index],
    ];
    persist(newHeaders);
  };

  const handleAddItem = (localId: string) => {
    persist(
      headers.map((h) =>
        h.localId === localId
          ? {
              ...h,
              Items: [
                ...h.Items,
                { CheckItemMessage: '', IsChecked: false, HeaderTitle: h.Title, IsTouched: false },
              ],
            }
          : h
      )
    );
  };

  const handleItemTextChange = (
    headerLocalId: string,
    itemIndex: number,
    value: string
  ) => {
    setHeaders((prev) =>
      prev.map((h) => {
        if (h.localId !== headerLocalId) return h;
        const newItems = [...h.Items];
        newItems[itemIndex] = { ...newItems[itemIndex], CheckItemMessage: value };
        return { ...h, Items: newItems };
      })
    );
  };

  const handleItemBlur = (headerLocalId: string, itemIndex: number) => {
    const newHeaders = headers.map((h) => {
      if (h.localId !== headerLocalId) return h;
      const newItems = [...h.Items];
      newItems[itemIndex] = { ...newItems[itemIndex], IsTouched: true };
      return { ...h, Items: newItems };
    });
    persist(newHeaders);
  };

  const handleItemIsInfoChange = (
    headerLocalId: string,
    itemIndex: number,
    value: boolean
  ) => {
    const newHeaders = headers.map((h) => {
      if (h.localId !== headerLocalId) return h;
      const newItems = [...h.Items];
      newItems[itemIndex] = { ...newItems[itemIndex], IsChecked: value };
      return { ...h, Items: newItems };
    });
    persist(newHeaders);
  };

  const handleMoveItem = (
    headerLocalId: string,
    itemIndex: number,
    direction: -1 | 1
  ) => {
    const newHeaders = headers.map((h) => {
      if (h.localId !== headerLocalId) return h;
      const newIndex = itemIndex + direction;
      if (newIndex < 0 || newIndex >= h.Items.length) return h;
      const newItems = [...h.Items];
      [newItems[itemIndex], newItems[newIndex]] = [
        newItems[newIndex],
        newItems[itemIndex],
      ];
      return { ...h, Items: newItems };
    });
    persist(newHeaders);
  };

  const handleDeleteItem = (headerLocalId: string, itemIndex: number) => {
    persist(
      headers.map((h) =>
        h.localId === headerLocalId
          ? { ...h, Items: h.Items.filter((_, i) => i !== itemIndex) }
          : h
      )
    );
  };

  return (
    <div className="tool-checkitems-panel">
      <div className="tool-checkitems-header d-flex align-items-center justify-content-between mb-3">
        <span className="tool-checkitems-title h6 mb-0 fw-bold">
          <FormattedMessage id="check_items" defaultMessage="Check Items" />
        </span>
        <div className="tool-checkitems-actions d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm tool-checkitems-template-select"
            value={selectedTemplateId}
            onChange={handleTemplateChange}
            style={{ width: '180px' }}
          >
            {allToolCheckItemTemplateOptions.map((template) => (
              <option key={template.id} value={template.id}>
                {template.id === -1 ? formatMessage(template.value) : template.value}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm text-white fw-medium"
            onClick={handleAddHeader}
            style={{ backgroundColor: '#f2994a', borderColor: '#f2994a', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <PlusIcon />
            <span><FormattedMessage id="add_header" defaultMessage="Add Header" /></span>
          </button>
        </div>
      </div>

      <div className="tool-checkitems-body">
        {headers.length === 0 ? (
          <div className="tool-checkitems-empty">
            <FormattedMessage id="no_headers_added" />
          </div>
        ) : (
          headers.map((header, hIndex) => {
            const isDuplicateHeaderTitle =
              !!header.Title.trim() &&
              headers.filter(
                (h) =>
                  h.Title.trim().toLowerCase() ===
                  header.Title.trim().toLowerCase()
              ).length > 1;
            const itemTexts = header.Items.map((i) =>
              i.CheckItemMessage.trim().toLowerCase()
            ).filter((t) => t !== '');
            const hasDuplicateInHeader =
              new Set(itemTexts).size !== itemTexts.length;

            return (
            <div
              key={header.localId}
              className="tool-checkitems-group mb-3 rounded overflow-hidden"
            >
              <div className="tool-checkitems-group-header d-flex align-items-center justify-content-between gap-2">
                {header.IsEditing ? (
                  <div className="tool-checkitems-header-title-input">
                    <input
                      autoFocus
                      className={`form-control ${isDuplicateHeaderTitle ? 'is-invalid' : ''}`}
                      value={header.Title}
                      placeholder={formatMessage('header_name_placeholder')}
                      onChange={(e) =>
                        handleHeaderTitleChange(header.localId, e.target.value)
                      }
                      onBlur={() => handleHeaderTitleBlur(header.localId)}
                      maxLength={100}
                    />
                    {isDuplicateHeaderTitle && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="duplicate_header_names_not_allowed" />
                      </div>
                    )}
                  </div>
                ) : (
                  <span
                    className={`tool-checkitems-group-title fw-medium show-Elipses ${isDuplicateHeaderTitle ? 'text-danger' : ''}`}
                    onClick={() =>
                      setHeaders((prev) =>
                        prev.map((h) =>
                          h.localId === header.localId
                            ? { ...h, IsEditing: true }
                            : h
                        )
                      )
                    }
                  >
                    {header.Title || <FormattedMessage id="header_name_placeholder" />}
                  </span>
                )}
                <div className="tool-checkitems-group-actions">
                  <button
                    type="button"
                    className="btn btn-icon btn-primary btn-sm"
                    onClick={() => handleAddItem(header.localId)}
                    title={formatMessage('add_item')}
                  >
                    <PlusIcon />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-light-primary btn-sm"
                    onClick={() => handleMoveHeader(hIndex, -1)}
                    disabled={hIndex === 0}
                    title="Move Up"
                  >
                    <UpIcon />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-light-primary btn-sm"
                    onClick={() => handleMoveHeader(hIndex, 1)}
                    disabled={hIndex === headers.length - 1}
                    title="Move Down"
                  >
                    <DownIcon />
                  </button>
                  <button
                    type="button"
                    className="btn-icon btn-light-danger btn-sm cross-header"
                    onClick={() => handleDeleteHeader(header.localId)}
                    title="Delete Header"
                  >
                    <RemoveIcon />
                  </button>
                </div>
              </div>
              <table className="table tool-checkitems-table mb-0">
                <tbody>
                  {header.Items.map((item, iIndex) => {
                    globalItemNo++;
                    const isTouched = item.IsTouched !== false && (!!item.IsTouched || !!submitAttempted);
                    const isEmpty = isTouched && item.CheckItemMessage.trim() === '';
                    const isDuplicate =
                      !isEmpty &&
                      hasDuplicateInHeader &&
                      header.Items.filter(
                        (i) =>
                          i.CheckItemMessage.trim().toLowerCase() ===
                          item.CheckItemMessage.trim().toLowerCase()
                      ).length > 1;

                    return (
                      <tr key={iIndex}>
                        <td className="text-muted text-center align-middle px-2" style={{ width: '24px', fontSize: '13px' }}>
                          {globalItemNo}
                        </td>
                        <td className="align-middle">
                          <input
                            className={`form-control form-control-sm ${isEmpty || isDuplicate ? 'is-invalid' : ''}`}
                            value={item.CheckItemMessage}
                            disabled={item.IsDisabled}
                            placeholder={formatMessage('check_item_content')}
                            onChange={(e) =>
                              handleItemTextChange(
                                header.localId,
                                iIndex,
                                e.target.value
                              )
                            }
                            onBlur={() => handleItemBlur(header.localId, iIndex)}
                          />
                          {isEmpty && (
                            <div className="invalid-feedback d-block errorMessage">
                              <FormattedMessage id="please_enter_check_content" />
                            </div>
                          )}
                          {isDuplicate && (
                            <div className="invalid-feedback d-block errorMessage">
                              <FormattedMessage id="duplicate_item_in_header" />
                            </div>
                          )}
                        </td>
                        <td className="text-end align-middle px-2" style={{ whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            className="form-check-input m-0 me-2 align-middle"
                            checked={!!item.IsChecked}
                            onChange={(e) =>
                              handleItemIsInfoChange(
                                header.localId,
                                iIndex,
                                e.target.checked
                              )
                            }
                            title={formatMessage('is_info')}
                          />
                          <button
                            type="button"
                            className="btn btn-icon btn-light-primary btn-sm me-1"
                            onClick={() => handleMoveItem(header.localId, iIndex, -1)}
                            disabled={iIndex === 0}
                            title={formatMessage('up')}
                          >
                            <UpIcon />
                          </button>
                          <button
                            type="button"
                            className="btn btn-icon btn-light-primary btn-sm me-1"
                            onClick={() => handleMoveItem(header.localId, iIndex, 1)}
                            disabled={iIndex === header.Items.length - 1}
                            title={formatMessage('down')}
                          >
                            <DownIcon />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-light-danger btn-sm cross-header"
                            onClick={() => handleDeleteItem(header.localId, iIndex)}
                            title="Delete"
                          >
                            <RemoveIcon />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
});

export default ToolCheckItemsPanel;
